import { proxy, subscribe } from "valtio/vanilla";
import { getStore, setStore } from "./api/storage.ts";
import { QueueEntry } from "./api/queue.ts";
import { TodoItem, TodoList, TodoListGroup } from "./api/types";
import { cacheListItems } from "./api/offline.ts";
import { User } from "@/api/user_api.ts";

interface LoginState {
	token?: string;
	shareToken?: string;
	user?: User;
}

interface UIState {
	editLists: boolean;
	editItems: boolean;
	editingList: TodoList | null;
	editingGroup: TodoListGroup | null;
	sharingList: TodoList | null;
	editingItem: TodoItem | null;
	newList: boolean;
	newGroup: boolean;
	deleteAccount: boolean;
}

const storedToken = getStore("token", undefined);
const storedQueue = getStore("queue", []);
const storedTaskCount = getStore("task_count", {});
const storedCollapsedGroups = getStore("collapsed_groups", {});

const urlParams = new URLSearchParams(window.location.search);
const shareToken = urlParams.get("share_token");

export const loginState = proxy<LoginState>({
	token: storedToken,
	shareToken: shareToken || undefined,
});
export const queueState = proxy<QueueEntry[]>(storedQueue);
export const listsState = proxy<{ lists: TodoList[] }>({ lists: [] });
export const groupsState = proxy<{ groups: TodoListGroup[] }>({ groups: [] });
export const itemsState = proxy<{ items: Record<string, TodoItem[]> }>({ items: {} });
export const collapsedGroupsState = proxy<{ groups: Record<string, boolean> }>({
	groups: storedCollapsedGroups,
});
export const uiState = proxy<UIState>({
	editItems: false,
	editLists: false,
	editingList: null,
	editingGroup: null,
	sharingList: null,
	editingItem: null,
	newList: false,
	newGroup: false,
	deleteAccount: false,
});

export const taskCountState = proxy<{
	lists: Record<string, { date: string; count: number }>;
}>({ lists: storedTaskCount });

subscribe(loginState, () => {
	setStore("token", loginState.token);
});
subscribe(queueState, () => {
	setStore("queue", queueState);
});
subscribe(listsState, () => {
	setStore("lists", listsState.lists);
});
subscribe(groupsState, () => {
	setStore("groups", groupsState.groups);
});
subscribe(collapsedGroupsState, () => {
	setStore("collapsed_groups", collapsedGroupsState.groups);
});
subscribe(taskCountState, () => {
	setStore("task_count", taskCountState.lists);
});
subscribe(itemsState.items, (operations) => {
	for (const op of operations) {
		const [action, path] = op;
		const listId = path[0] as string;
		cacheListItems(listId, itemsState.items[listId]);
	}
});

type TodoModel = TodoList | TodoListGroup | TodoItem;

export const addStateEntry = (stateArr: TodoModel[], value: TodoModel, skipIfExists: boolean) => {
	if (skipIfExists) {
		const exists = stateArr.find((l) => l.id === value.id);
		if (exists) return;
	}
	stateArr.push(value);
};

export const deleteStateEntry = (state: any, key: string, value: any) => {
	state[key] = state[key].filter((l: any) => l.id !== value.id);
};

export const setStateEntry = (
	stateArr: TodoModel[],
	value: TodoModel,
	method: "merge" | "set" = "set",
	upsert: boolean = false,
) => {
	const index = stateArr.findIndex((l) => l.id === value.id);
	if (index === -1) {
		if (!upsert) return;
		stateArr.push(value);
		return;
	}
	if (method === "set") {
		stateArr[index] = value;
	} else {
		stateArr[index] = { ...stateArr[index], ...value };
	}
};

export const updateTaskCounts = (listId: string) => {
	listsState.lists.forEach((l) => {
		if (l.id === listId) {
			taskCountState.lists[listId] = {
				date: new Date().toISOString(),
				count: itemsState.items[listId].filter((i) => !i.completed).length,
			};
		}
	});
};
